import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Building2, Layers, TrendingUp, Wallet } from 'lucide-react';
import { useDepartmentsSummary } from '@/api/hooks/useDepartments';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeviationSection } from '@/components/departments/DeviationSection';

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const RPL = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

export default function Departments() {
  const { data, isLoading, error } = useDepartmentsSummary();

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Departments</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Cost-centre spend rollup — top departments by 90-day fleet spend.
        </p>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load departments data: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          title="Active cost centres (90d)"
          icon={<Layers className="h-4 w-4 text-muted-foreground" />}
          value={data ? NUM.format(data.kpis.active_cost_centres) : null}
          loading={isLoading}
        />
        <Kpi
          title="Top cost centre (90d)"
          icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
          value={data ? data.kpis.top_cost_centre_label ?? '—' : null}
          loading={isLoading}
          subtle={
            data
              ? ZAR.format(data.kpis.top_cost_centre_spend_rands)
              : undefined
          }
        />
        <Kpi
          title="Total fleet spend (90d)"
          icon={<Wallet className="h-4 w-4 text-muted-foreground" />}
          value={data ? ZAR.format(data.kpis.total_spend_rands) : null}
          loading={isLoading}
        />
        <Kpi
          title="Avg spend / cost centre"
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          value={
            data ? ZAR.format(data.kpis.avg_spend_per_cost_centre_rands) : null
          }
          loading={isLoading}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Top 20 cost centres by 90-day spend
          </CardTitle>
        </CardHeader>
        {/* 20 categories need vertical room: at 28rem each band was ~22px and
            the 140px gutter wrapped every label onto 4 lines, so adjacent
            labels collided. ~34px per band + a wider, single-line gutter with
            ellipsis fixes it; the full untruncated label lives in the tooltip. */}
        <CardContent className="h-[46rem] px-2 py-2">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data?.top_cost_centres ?? []}
                layout="vertical"
                margin={{ top: 4, right: 32, bottom: 4, left: 8 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: number) =>
                    v >= 1_000_000
                      ? `${(v / 1_000_000).toFixed(1)}M`
                      : v >= 1_000
                        ? `${(v / 1_000).toFixed(0)}k`
                        : `${v}`
                  }
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  width={230}
                  interval={0}
                  tickFormatter={(v: string) =>
                    v.length > 30 ? `${v.slice(0, 29)}…` : v
                  }
                />
                <Tooltip
                  formatter={(v: number) => ZAR.format(v)}
                  labelFormatter={(l: string) => l}
                />
                <Bar dataKey="spend_rands" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="flex-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Top 20 — detail</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0">
          {isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cost centre</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Province</TableHead>
                  <TableHead className="text-right">Vehicles</TableHead>
                  <TableHead className="text-right">Txns (90d)</TableHead>
                  <TableHead className="text-right">Spend (90d)</TableHead>
                  <TableHead className="text-right">Avg R/L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.top_cost_centres.map((d) => (
                  <TableRow key={d.cost_centre_code}>
                    <TableCell className="font-mono text-xs">
                      {d.cost_centre_code}
                    </TableCell>
                    <TableCell className="text-sm">
                      {d.district_desc ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {d.province_desc ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {NUM.format(d.vehicle_count)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {NUM.format(d.txn_count)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ZAR.format(d.spend_rands)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {d.avg_rand_per_litre != null
                        ? `R ${RPL.format(d.avg_rand_per_litre)}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {data && data.top_cost_centres.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      No cost-centre activity in the last 90 days.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <DeviationSection />
    </div>
  );
}

interface KpiProps {
  title: string;
  value: string | null;
  icon: React.ReactNode;
  loading: boolean;
  subtle?: string;
}

function Kpi({ title, value, icon, loading, subtle }: KpiProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-1.5 pt-3">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="pb-3 pt-0">
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <div className="truncate text-2xl font-semibold tabular-nums">
            {value ?? '—'}
          </div>
        )}
        {subtle && (
          <div className="mt-1 text-xs text-muted-foreground">{subtle}</div>
        )}
      </CardContent>
    </Card>
  );
}
